import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Divider, TextField, Typography } from '@mui/material';
import CodeChip from '../coding/CodeChip';
import CodePicker from '../coding/CodePicker';
import ConfirmDialog from '../coding/ConfirmDialog';
import { utCodesFor, rcCodes } from '../coding/codes';
import { finishTieBreak } from '../../services/adjudicationService';
import useCodingKeys from './useCodingKeys';
import { DIMENSION_NAMES, KeysHint, Panel } from './parts';

const DIMS = ['ut', 'rc'];
const NAME = { ut: 'UT', rc: 'RC' };
const TIE = { color: '#6640A6', background: '#F2EDFC' };
const AGREED = { color: '#1F6B31', background: '#E6F4EA' };

// The two codes agree on every tied dimension: the tie came from an unsure mark (tieOnUnsure runs)
const tiedOnUnsure = (view) => view.tiedDimensions.every((d) => view.codes[0]?.[d] === view.codes[1]?.[d]);

export const tieMessage = (view) => {
    if (tiedOnUnsure(view)) return 'One adjudicator marked this item unsure, so in this run it goes to a tie-break although the codes agree.';
    if (view.tiedDimensions.length === 2) return 'The two adjudicators disagree on UT and RC. Your codes decide both.';
    const [tied] = view.tiedDimensions;
    const other = DIMS.find((d) => d !== tied);
    return `The two adjudicators disagree on ${NAME[tied]}. Your code decides it; ${NAME[other]} is already agreed.`;
};

// The purple band: the only header difference of a tie item (wf-adj-item-tie-1)
export const TieBand = ({ view }) => (
    <Box
        role="note"
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, mb: 2, borderRadius: 2, border: `1px solid ${TIE.color}`, bgcolor: TIE.background, flexWrap: 'wrap' }}
    >
        <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: TIE.color, color: '#FFFFFF', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em' }}>
            TIE-BREAK
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{tieMessage(view)}</Typography>
    </Box>
);

// Codes so far: not blind. The two locked codes as Code 1 and Code 2, without names or slots, their
// reasons and notes, and the earlier human label. Never model or LLM output or the flag.
export const TieCodes = ({ view }) => {
    const earlierName = view.earlierKind === 'annotation' ? 'Annotation label' : 'Original label';
    return (
        <Panel title="Codes so far">
            <Box role="table" aria-label="Codes so far" sx={{ display: 'grid', gap: 0.75 }}>
                <Box role="row" sx={{ display: 'grid', gridTemplateColumns: '40px repeat(3, 1fr) 56px', gap: 1, px: 1 }}>
                    <span role="columnheader" />
                    {['Code 1', 'Code 2', earlierName].map((h) => (
                        <Typography key={h} role="columnheader" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{h}</Typography>
                    ))}
                    <span role="columnheader" />
                </Box>
                {DIMS.map((dim) => {
                    const tied = view.tiedDimensions.includes(dim);
                    return (
                        <Box key={dim} role="row" sx={{ display: 'grid', gridTemplateColumns: '40px repeat(3, 1fr) 56px', gap: 1, alignItems: 'center', px: 1, py: 0.75, borderRadius: 1.5, bgcolor: tied ? TIE.background : 'transparent' }}>
                            <Typography role="rowheader" variant="body2" sx={{ fontWeight: 700 }}>{NAME[dim]}</Typography>
                            {view.codes.map((c) => <Box key={c.label} role="cell"><CodeChip code={c[dim]} coded /></Box>)}
                            <Box role="cell" sx={{ display: 'flex', gap: 0.5 }}>
                                {view.earlier.map((e, i) => <CodeChip key={i} code={e[dim]} coded />)}
                            </Box>
                            <Typography role="cell" variant="caption" sx={{ fontWeight: 600, textAlign: 'right', color: tied ? TIE.color : AGREED.color }}>
                                {tied ? 'tied' : 'agreed'}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'grid', gap: 1.5 }}>
                {view.codes.map((c) => {
                    const reasons = DIMS.filter((d) => c.reasons?.[d]);
                    return (
                        <Box key={c.label}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {c.label}{c.unsure ? ' · marked unsure' : ''}
                            </Typography>
                            {reasons.map((d) => (
                                <Typography key={d} variant="body2">{NAME[d]} reason: {c.reasons[d].label}</Typography>
                            ))}
                            {c.note && <Typography variant="body2" color="text.secondary">“{c.note}”</Typography>}
                            {!reasons.length && !c.note && (
                                <Typography variant="body2" color="text.secondary">No reasons: same as the earlier label</Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Panel>
    );
};

// Your tie-break (wf-adj-item-tie 1–4): agreed dimensions read-only, tied ones coded with any code the
// codebook allows for the speaker, no reason codes, an optional note, and a confirm step.
const TieStep = ({ runId, view, codebook, onPicksChange, onDone, onMovedOn }) => {
    const [codes, setCodes] = useState({});
    const [note, setNote] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const tied = view.tiedDimensions;
    const lists = { ut: utCodesFor(codebook, view.item.speaker), rc: rcCodes(codebook) };
    const ready = tied.every((d) => codes[d]);

    useEffect(() => {
        const inPlay = (dim) => [...new Set([...view.codes.map((c) => c[dim]), codes[dim]].filter(Boolean))];
        onPicksChange?.({ ut: inPlay('ut'), rc: inPlay('rc') });
    }, [codes, view, onPicksChange]);

    useCodingKeys({
        enabled: !confirming,
        utCodes: tied.includes('ut') ? lists.ut.map((e) => e.code) : [],
        rcCodes: tied.includes('rc') ? lists.rc.map((e) => e.code) : [],
        onUt: (code) => setCodes((c) => ({ ...c, ut: code })),
        onRc: (code) => setCodes((c) => ({ ...c, rc: code })),
        onEnter: () => ready && setConfirming(true),
    });

    const finish = async () => {
        setBusy(true);
        setError(null);
        try {
            await finishTieBreak(runId, view.item.id, { codes, note: note.trim() || undefined });
            onDone();
        } catch (err) {
            setBusy(false);
            if (err.response?.status === 409) {
                setConfirming(false);
                onMovedOn();
            } else {
                setError(err.response?.status === 400 ? 'That code isn’t allowed here. Pick again.' : 'Couldn’t save the tie-break. Try again.');
            }
        }
    };

    const finalCodes = DIMS.map((d) => `${NAME[d]} ${tied.includes(d) ? codes[d] : view.agreed[d]}`).join(' · ');

    return (
        <Panel title="Your tie-break">
            <Box sx={{ display: 'grid', gap: 2.5 }}>
                {DIMS.map((dim) => (tied.includes(dim) ? (
                    <CodePicker
                        key={dim}
                        id={`tie-${dim}`}
                        stacked
                        label={`${DIMENSION_NAMES[dim]} · tied`}
                        codes={lists[dim]}
                        value={codes[dim] || null}
                        onChange={(code) => setCodes((c) => ({ ...c, [dim]: code }))}
                        hint="Any code in the codebook, not only the two given"
                    />
                ) : (
                    <Box key={dim}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>{DIMENSION_NAMES[dim]}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 0.75, borderRadius: 1.5, bgcolor: AGREED.background }}>
                            <CodeChip code={view.agreed[dim]} coded />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: AGREED.color }}>Agreed by both · final, not coded again</Typography>
                        </Box>
                    </Box>
                )))}
            </Box>

            <TextField
                label="Note (optional)"
                placeholder="Why you decided this way"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                inputProps={{ maxLength: 2000 }}
                sx={{ mt: 2.5 }}
            />
            <Button variant="contained" size="large" fullWidth disabled={!ready} onClick={() => setConfirming(true)} sx={{ mt: 2.5, minHeight: 46 }}>
                Finish tie-break
            </Button>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.25 }}>
                {ready ? 'Your code becomes the final label for the tied dimensions.' : 'Choose a code for each tied dimension to finish'}
            </Typography>
            <KeysHint>letter for UT · 1–5 for RC · Enter to finish</KeysHint>

            <ConfirmDialog
                open={confirming}
                title="Finish the tie-break?"
                confirmLabel="Finish tie-break"
                cancelLabel="Back"
                busy={busy}
                error={error}
                onConfirm={finish}
                onClose={() => setConfirming(false)}
            >
                <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 1 }}>{finalCodes}</Typography>
                <Typography variant="body2">
                    These become the final label for this utterance on UT and RC. The tie-break can’t be changed afterwards.
                </Typography>
            </ConfirmDialog>
            {error && !confirming && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Panel>
    );
};

export default TieStep;
